import type { FieldTree } from '@angular/forms/signals';

import type { ShadowSettingsLegacy } from '@sites/entity-core';

/**
 * Minimal node shape a FieldTree must satisfy to install
 * {@link shadowPlugin} — the `isShadow` toggle plus the settings bag emitted
 * by the host's `sp-shadow-settings` component. Schema twin in entity-core:
 * `shadowPluginShapeLegacy` (`widget/common/schemas/legacy/shadow.ts`) — spread it
 * into the node's schema.
 *
 * Both keys are `nullish()` in every schema that carries them (they
 * post-date the widgets), so persisted nodes may omit them entirely.
 */
export type ShadowPluginData = {
    isShadow?: boolean | null;
    shadowSettings?: ShadowSettingsLegacy | null;
};

export type ShadowApi = {
    /** `undefined` while the key is absent from the persisted node (nullish schema). */
    readonly isShadow: FieldTree<boolean | null | undefined> | undefined;
    /** `undefined` while the key is absent from the persisted node (nullish schema). */
    readonly shadowSettings: FieldTree<ShadowSettingsLegacy | null | undefined> | undefined;
    setIsShadow: (value: boolean) => void;
    setShadowSettings: (value: ShadowSettingsLegacy | null) => void;
};

/**
 * Shadow toggle + settings bag as FieldTree accessors + setter sugar.
 *
 * Binds to **any** FieldTree node, not just widget `data` — pass
 * `() => _raw.data` for data-level shadows or e.g.
 * `() => form.buttonAt(i)` for per-item shadows (button items carry the
 * same pair).
 *
 * Both keys are optional zod branches, which hit the missing-key trap on
 * WRITE (the FieldTree child never materialises for keys absent from the
 * model, so `field().value.set()` throws) — the setters therefore always
 * write **through the parent node**, which also covers nodes persisted
 * before the shadow feature shipped. The accessors return `undefined` at
 * runtime while the key is absent.
 *
 * Receives the node **lazily** and exposes getters, so tree paths only
 * materialise on consumer access — compose with
 * {@link import('../../core/compose-form').composeForm}, never object spread.
 */
export function shadowPlugin<D extends ShadowPluginData>(node: () => FieldTree<D>): ShadowApi {
    // FieldTree is invariant (writable signals), so the narrowing is done via
    // an explicit cast — safe by the `D extends ShadowPluginData` constraint.
    const n = node as unknown as () => FieldTree<ShadowPluginData>;
    return {
        get isShadow() {
            return n().isShadow;
        },
        setIsShadow: (value: boolean) => {
            n()().value.update((parent) => ({ ...parent, isShadow: value }));
        },
        get shadowSettings() {
            return n().shadowSettings;
        },
        setShadowSettings: (value: ShadowSettingsLegacy | null) => {
            n()().value.update((parent) => ({ ...parent, shadowSettings: value }));
        },
    };
}
